'use client';

import Link from 'next/link';
import BookDemoSection from '@/components/BookDemoSection';
import { BookDemoButton, Button } from '@/components/ui';
import { useTranslation } from '@/hooks/useTranslation';
import { Radio, Share2, Headphones, Search, MapPin, Radar, BarChart3, Plug, Gauge, Microscope } from 'lucide-react';
const CAPABILITIES = [{
  icon: Radio,
  slug: 'social-intelligence',
  title: 'Social Intelligence',
  desc: 'Monitor brand mentions, decode audience sentiment, and track narrative shifts across 300+ digital sources, channels, and markets.',
  tags: ['Real-time', '300+ sources', 'NLP']
}, {
  icon: Share2,
  slug: 'social-media-management',
  title: 'Social Media Management',
  desc: 'Plan, publish, engage, and measure all from one place. Manage campaigns across Facebook, Instagram, LinkedIn, X and more with AI-assisted responses.',
  tags: ['Publishing', 'Scheduling', 'Engagement']
}, {
  icon: Headphones,
  slug: 'holistic-digital-care',
  title: 'Holistic Digital Care',
  desc: 'Deliver end-to-end customer care across social, chat, email, and review platforms. AI triage, smart routing, SLA, and agent assist tools ensure every customer gets a fast response.',
  tags: ['Unified inbox', 'SLA', 'AI routing']
}, {
  icon: Search,
  slug: 'market-research',
  title: 'Market Research',
  desc: 'Go beyond vanity metrics. Surface-deep consumer insights, identify emerging patterns, and understand the "why" behind audience behaviour.',
  tags: ['Consumer insights', 'Trends', 'Segments']
}, {
  icon: MapPin,
  slug: 'location-intelligence',
  title: 'Location Intelligence',
  desc: 'Get full visibility into outlet-level performance across regions. Benchmark sentiment, review scores, and data platform integrations so you can act locally while managing at scale.',
  tags: ['Geo insights', 'Multi-location', 'City-level']
}, {
  icon: Radar,
  slug: 'command-centre',
  title: 'Command Centre',
  desc: 'The CX war room your teams will rely on. Live intelligence and unified clarity. Real-time dashboards for CX, brand, ops, and leadership.',
  tags: ['War room', 'Real-time', 'Crisis']
}, {
  icon: BarChart3,
  slug: 'competitive-benchmarking',
  title: 'Competitive Benchmarking',
  desc: 'Know exactly where you stand. Surface competitor sentiment, share of voice, campaign performance, and channel dominance in real time.',
  tags: ['Share of voice', 'Competitive', 'Benchmark']
}, {
  icon: Plug,
  slug: 'integrations',
  title: 'Integrations',
  desc: 'Connect Locobuzz seamlessly with the tools you already use. With 57+ CRM, helpdesk, and data platform integrations, your existing stack becomes exponentially more powerful.',
  tags: ['CRM', 'Helpdesk', 'API']
}, {
  icon: Gauge,
  kind: 'solution',
  slug: 'business-intelligence',
  title: 'Business Intelligence',
  desc: 'Transform raw CX data into boardroom-ready intelligence. Custom dashboards, cross-channel reporting, and AI-synthesised insights give leadership the clarity they need.',
  tags: ['Dashboards', 'Analytics', 'Reports']
}, {
  icon: Microscope,
  kind: 'solution',
  slug: 'forensic-audit',
  title: 'Forensic Audit',
  desc: 'Go deep when it matters most. Granular analysis of past interactions, escalation patterns, and compliance gaps helping brands understand what went wrong.',
  tags: ['Audit', 'Compliance', 'Risk']
}];
const STATS = [{
  n: '350+',
  label: 'Enterprise Clients Globally'
}, {
  n: '57+',
  label: 'CRM & Platform Integrations'
}, {
  n: '25+',
  label: 'Social & Non-Social Channels'
}, {
  n: 'Real-Time',
  label: 'AI at the Core™'
}];
export default function PlatformPage() {
  const {
    t
  } = useTranslation();
  return <>
      {/* Hero */}
      <section style={{
      background: '#fff',
      position: 'relative',
      overflow: 'hidden',
      padding: 'var(--hero-pad)'
    }}>
        <div className="lb-hero-grad" style={{
        position: 'absolute',
        inset: 0
      }} />
        <div className="container" style={{
        position: 'relative',
        zIndex: 1,
        textAlign: 'center'
      }}>
          <span className="lb-pill" style={{
          marginBottom: 16
        }}>{t("platform.locobuzz_platform")}</span>
          <h1 className="lb-h1" style={{
          maxWidth: 960,
          margin: '0 auto 20px'
        }}>
            {t("platform.your_complete_aipowered_social_list")}<br />{t("platform.cx_platform")}{' '}
            <span className="serif-accent italic" style={{ color: 'var(--brand)' }}>{t("platform.one_enterprise_suite_unified")}</span>
          </h1>
          <p style={{
          fontSize: 18,
          color: '#4B5563',
          maxWidth: 680,
          margin: '0 auto 36px',
          lineHeight: 1.7
        }}>
            {t("platform.locobuzz_unified_cx_connects_your_m")}
          </p>
          <div style={{
          display: 'flex',
          gap: 14,
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
            <BookDemoButton label={t("platform.book_a_demo")} />
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section style={{
      background: '#F8FAFF',
      padding: '32px 0',
      borderTop: '1px solid #E5E7EB',
      borderBottom: '1px solid #E5E7EB'
    }}>
        <div className="container">
          <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4,1fr)',
          gap: 24
        }}>
            {STATS.map(s => <div key={s.n} style={{
            textAlign: 'center'
          }}>
                <div style={{
              fontFamily: "'Inter',sans-serif",
              fontSize: 36,
              fontWeight: 900,
              color: '#0098FF',
              lineHeight: 1,
              marginBottom: 6
            }}>{s.n}</div>
                <div style={{
              fontSize: 13,
              color: '#6B7280'
            }}>{s.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Why Unified CX */}
      <section className="lb-section">
        <div className="container" style={{
        textAlign: 'center',
        maxWidth: 720,
        margin: '0 auto'
      }}>
          <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: '#6B7280',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          marginBottom: 12
        }}>{t("platform.why_unified_cx")}</div>
          <h2 className="lb-h2" style={{
          marginBottom: 16
        }}>{t("platform.fragmented_tools_cost_you_speed_con")}</h2>
          <p style={{
          fontSize: 17,
          color: '#4B5563',
          lineHeight: 1.75
        }}>
            {t("platform.when_your_marketing_team_is_on_one")}
          </p>
        </div>
      </section>

      {/* Capabilities grid */}
      <section className="lb-section-alt">
        <div className="container">
          <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 40,
          flexWrap: 'wrap',
          gap: 16
        }}>
            <div>
              <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#6B7280',
              marginBottom: 8,
              letterSpacing: '0.5px',
              textTransform: 'uppercase'
            }}>{t("platform.explore_every_capability")}</div>
              <h2 className="lb-h2" style={{
              margin: 0
            }}>{t("platform.one_platform_every_cx_need")}</h2>
            </div>
            <div style={{
            fontSize: 13,
            color: '#6B7280',
            maxWidth: 280
          }}>{t("platform.click_into_any_module_to_see_how_it")}</div>
          </div>
          <div className="lb-grid-3">
            {CAPABILITIES.map((cap, i) => {
            const Icon = cap.icon;
            const isSolution = cap.kind === 'solution';
            const href = isSolution ? '/solutions/' + cap.slug : '/platform/' + cap.slug;
            return <Link key={i} href={href} style={{
            textDecoration: 'none'
          }}>
                <div className="lb-card" style={{
              height: '100%'
            }}>
                  <div className="lb-icon-box" style={{
                marginBottom: 14
              }}><Icon size={22} strokeWidth={1.8} color="#0098FF" /></div>
                  <div style={{
                fontSize: 11,
                fontWeight: 700,
                color: '#9CA3AF',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                marginBottom: 6
              }}>{t(isSolution ? "platform.solution" : "platform.capability")}</div>
                  <h3 className="lb-h3" style={{
                fontSize: 17,
                marginBottom: 10
              }}>{cap.title}</h3>
                  <p style={{
                fontSize: 13,
                color: '#6B7280',
                lineHeight: 1.65,
                marginBottom: 14
              }}>{cap.desc}</p>
                  <div style={{
                display: 'flex',
                gap: 6,
                flexWrap: 'wrap',
                marginBottom: 14
              }}>
                    {cap.tags.map(tag => <span key={tag} style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#0098FF',
                  background: '#EBF4FF',
                  padding: '3px 10px',
                  borderRadius: 100
                }}>{tag}</span>)}
                  </div>
                  <div style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#0098FF'
              }}>{t("platform.learn_more")}</div>
                </div>
              </Link>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
      background: 'linear-gradient(135deg,#EAF3FF 0%,#F2F8FF 40%,#EAF6FF 100%)',
      padding: 'var(--section-pad-tight)',
      textAlign: 'center'
    }}>
        <div className="container">
          <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: 'var(--brand)',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          marginBottom: 16
        }}>{t("platform.ready_to_unify_your_cxm")}</div>
          <h2 style={{
          fontFamily: "'Inter',sans-serif",
          fontWeight: 900,
          fontSize: 'clamp(26px,4vw,48px)',
          color: '#0D1117',
          lineHeight: 1.1,
          marginBottom: 16
        }}>
            {t("platform.join_400_marketing_and_cx_teams_glo")}
          </h2>
          <Button href="#book-demo" variant="cta" size="lg">{t("platform.try_locobuzz")}</Button>
        </div>
      </section>

      <BookDemoSection />
    </>;
}